import { DependencyList, useEffect, useState } from 'react';
import { JC_LOADING, JcLoading } from '../utils/JcLoading';
import { JcUnsubscribe } from '../utils/JcUnsubscribe';
import { JcDataError } from './JcDataError';
import { JcAsyncResult } from './JcAsyncResult';
import { JcSnapshotMetadata, confirmedAbsence } from './JcSnapshotMetadata';

/**
 * Shared engine for snapshot hooks: seeds `JC_LOADING`, resets on dep change or
 * retry, and surfaces value/error/retry as a {@link JcAsyncResult}. `retry`
 * bumps an internal nonce to force a fresh subscription.
 */
const useSnapshot = <V>(
    subscribe: (emit: (value: V | JcLoading) => void, onError: (error: JcDataError) => void) => JcUnsubscribe,
    deps: DependencyList,
): JcAsyncResult<V> => {
    const [value, setValue] = useState<V | JcLoading>(JC_LOADING);
    const [error, setError] = useState<JcDataError | null>(null);
    const [nonce, setNonce] = useState(0);

    useEffect(() => {
        setValue(JC_LOADING);
        setError(null);
        return subscribe(setValue, setError);
        // `subscribe` is tracked via `deps`; `nonce` forces a re-subscribe on retry.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps, nonce]);

    return { value, error, retry: () => setNonce((n) => n + 1) };
};

/** Subscription hook for list/value snapshots (value is never `null`). */
export const useJcSnapshot = <V>(
    subscribe: (onValue: (value: V) => void, onError: (error: JcDataError) => void) => JcUnsubscribe,
    deps: DependencyList,
): JcAsyncResult<V> => useSnapshot<V>((emit, onError) => subscribe(emit, onError), deps);

export interface JcDocSnapshotOptions {
    /** Map a cached miss to `JC_LOADING` so `null` means server-confirmed absence. */
    readonly confirmAbsence?: boolean;
}

/**
 * Subscription hook for single-document snapshots. Absence is modeled in the
 * value: the result is `JcAsyncResult<T | null>`, so `value` is `T`, `null`
 * (absent), or `JC_LOADING`.
 */
export const useJcDocSnapshot = <T>(
    subscribe: (
        onValue: (item: T | null, metadata: JcSnapshotMetadata) => void,
        onError: (error: JcDataError) => void,
    ) => JcUnsubscribe,
    deps: DependencyList,
    options?: JcDocSnapshotOptions,
): JcAsyncResult<T | null> =>
    useSnapshot<T | null>(
        (emit, onError) => subscribe(
            (item, metadata) => emit(options?.confirmAbsence ? confirmedAbsence(item, metadata) : item),
            onError,
        ),
        deps,
    );
